import {
  BodyText,
  DropdownValue,
  FlexColumn,
  FlexRow,
  SvgIcon,
  Textarea
} from '@make-software/cspr-design';
import React, { useEffect, useState } from 'react';
import {
  DropdownStyles,
  ErrorText,
  ErrorTextContainer,
  StyledConfirmButton,
  StyledDismissButton,
  StyledInput
} from 'app/components/common/common-styles';
import { EntityType, getPluginsList } from 'app/console-api/plugins/plugins';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { HttpError } from 'app/components/utils/http-error';
import { customErrorMessage, statusCode } from 'app/constants';
import { SIGN_IN_PATH } from 'app/route/paths';
import SuccessEntityWindow from 'app/components/common/success-entity-modal/success-entity-modal';
import CreateSuccessIcon from 'assets/logos/success-entity.svg';
import { ErrorResult } from 'app/organizations-api/types';
import { ErrorWindow } from 'app/components/common/error-window/error-window';
import NotFoundIcon from 'assets/logos/no-found.svg';
import { pluginFormControlMap } from 'app/components/management/common/plugins/form-control-map';
import { array, number, object, string } from 'yup';
import { createTask } from 'app/console-api';
import ErrorEntityWindow from 'app/components/common/error-entity-modal/error-entity-modal';
import { RewardValueControl } from 'app/components/management/common/rewards/rewards-value-control';
import { RewardsType, RewardsValue } from 'app/console-api/apps/types';
import { AppCategory, getAppCategories } from 'app/console-api/apps/categories';
import { AppCategoriesSelect } from 'app/components/management/common/app-categories-select/app-categories-select';
import { useTabsCtx } from 'app/components/management/apps/tabs-page-layout';
import { getRewardsTypes } from 'app/console-api/apps/rewards';
import {
  automationBlockValidation,
  AutomationsControlsBlock
} from 'app/components/management/common/autoamtions/automations-controls-block';
import { AutomationRequest } from 'app/console-api/automations/types';
import Select from 'react-select';

const validationSchema = object().shape({
  name: string().required('Name is required').max(48),
  description: string().max(255),
  plugin: object().shape({
    label: string().required('At least one plugin should be selected'),
    value: string().required('At least one plugin should be selected'),
    id: number().required('At least one plugin should be selected')
  }),
  customMetadata: string()
    .nullable()
    .test('is-json', 'Must be a valid JSON object', (value) => {
      if (!value) return true;
      try {
        const parsed = JSON.parse(value);
        return typeof parsed === 'object' && !Array.isArray(parsed);
      } catch (e) {
        return false;
      }
    }),
  rewards: array()
    .of(
      object().shape({
        rewardID: number(),
        value: number()
          .required('Reward value is required')
          .min(1, 'Reward value must be greater than 0')
      })
    )
    .test(
      'unique-types',
      'Reward types must be unique. You have duplicate reward types.',
      (rewards) => {
        if (!Array.isArray(rewards)) return true;
        const types = rewards.map((r) => r.rewardID);
        return new Set(types).size === types.length;
      }
    ),
  categories: array().min(1, 'At least one category should be selected'),
  automations: automationBlockValidation
});

export interface PluginDropdownValue extends DropdownValue {
  id: number;
}

export const TaskCreateForm: React.FC = () => {
  const { app } = useTabsCtx();
  const [pluginList, setPluginList] = useState<PluginDropdownValue[]>([]);
  const [openSuccessModal, setOpenSuccessModal] = useState<boolean>(false);
  const [unexpectedError, setUnexpectedError] = useState<ErrorResult | null>(null);
  const [openConflictErrorModal, setOpenConflictErrorModal] = useState<boolean>(false);
  const [conflictError, setConflictError] = useState<string>('');
  const [appCategories, setAppCategories] = useState<AppCategory[]>([]);
  const [rewardsTypes, setRewardsTypes] = useState<RewardsType[]>([]);

  const [formData, setFormData] = useState({
    plugin: { value: '', label: '', id: 0 },
    name: '',
    description: '',
    pluginMetadata: {},
    customMetadata: '',
    rewards: [] as RewardsValue[],
    categories: [] as number[],
    automations: [] as AutomationRequest[]
  });

  const SelectedFormControls = pluginFormControlMap[formData.plugin.id];

  const navigate = useNavigate();

  const {
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(
      SelectedFormControls
        ? validationSchema.concat(SelectedFormControls.validationSchema)
        : validationSchema
    ),
    defaultValues: formData
  });

  useEffect(() => {
    if (!app) {
      return;
    }
    getRewardsTypes(app.id)
      .then((response) => {
        setRewardsTypes(response.data || []);
        handleInputChange('rewards', [{ rewardID: response.data[0].id, value: 100 }]);
      })
      .catch((error: any) => {
        console.error('Failed to fetch rewards types', error);
      });

    getPluginsList(EntityType.Task)
      .then((plugins) => {
        const res = plugins.map((plugin) => {
          return {
            label: plugin.name,
            value: plugin.id.toString(),
            id: plugin.id
          };
        });
        setPluginList(res || []);
      })
      .catch((error: any) => {
        if (error instanceof HttpError) {
          if (error.statusCode === statusCode.authentication_error) {
            window.location.href = SIGN_IN_PATH;
            return;
          }
        }
        console.error('Failed to fetch plugins:', error);
      });

    getAppCategories(app.id)
      .then((response) => {
        setAppCategories(response.data || []);
      })
      .catch((error: any) => {
        if (error instanceof HttpError) {
          setUnexpectedError(customErrorMessage);
        }
        console.error('Failed to load app categories:', error);
      });
  }, [app]);

  const handleInputChange = <K extends keyof typeof formData>(
    key: K,
    value: (typeof formData)[K]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value
    }));
    // @ts-ignore
    setValue(key, value);

    // @ts-ignore
    clearErrors(key);
  };

  const handleSubmitData = () => {
    createTask(app?.id!, {
      name: formData.name,
      description: formData.description,
      pluginMetadata: formData.pluginMetadata,
      customMetadata: formData.customMetadata ? JSON.parse(formData.customMetadata) : null,
      pluginID: formData.plugin.id,
      rewards: formData.rewards,
      categories: formData.categories || [],
      automations: formData.automations
    })
      .then(() => {
        setOpenSuccessModal(true);
      })
      .catch((error: any) => {
        if (error instanceof HttpError) {
          switch (error.statusCode) {
            case statusCode.validation_error:
              setUnexpectedError({
                code: error.statusCode.toString(),
                message: error.message
              });
              break;
            case statusCode.conflict_error:
              setConflictError(error.info?.error.description || error.message);
              setOpenConflictErrorModal(true);
              break;
            case statusCode.authentication_error:
              window.location.href = SIGN_IN_PATH;
              return;
            case statusCode.unexpected_error:
              setUnexpectedError(customErrorMessage);
              break;
            default:
              setUnexpectedError(customErrorMessage);
              break;
          }
        } else {
          setUnexpectedError(customErrorMessage);
        }

        console.error('Failed to create task:', error);
      });
  };

  const handleCloseConflictErrorModal = () => {
    setOpenConflictErrorModal(false);
  };

  const sentData = () => {
    handleSubmit(handleSubmitData)();
  };

  const handleOnDismiss = () => {
    clearErrors();
    navigate(-1);
  };

  const handleCloseSuccessModal = () => {
    setOpenSuccessModal(false);
    navigate(-1);
  };

  if (unexpectedError) {
    return (
      <ErrorWindow
        withModal
        title={unexpectedError.code}
        information={unexpectedError.message}
        icon={<SvgIcon src={NotFoundIcon} width={219} height={125} />}
      />
    );
  }

  return (
    <FlexColumn gap={40}>
      <SuccessEntityWindow
        dismissLabel={'Close'}
        onDismiss={handleCloseSuccessModal}
        isOpen={openSuccessModal}
        icon={<SvgIcon src={CreateSuccessIcon} width={195} height={120} />}
        title="Task has been created"
        information="The task was successfully created."
      />
      <ErrorEntityWindow
        dismissLabel={'Close'}
        onDismiss={handleCloseConflictErrorModal}
        isOpen={openConflictErrorModal}
        icon={<SvgIcon src={NotFoundIcon} width={195} height={120} />}
        title={conflictError}
        information={''}
      />
      <FlexColumn gap={10}>
        <FlexColumn gap={4}>
          <BodyText size={1}>Task type</BodyText>
          <Select
            value={formData.plugin.value === '' ? null : formData.plugin}
            placeholder={<BodyText size={3}>Select task type</BodyText>}
            options={pluginList}
            styles={DropdownStyles()}
            onChange={(e) => handleInputChange('plugin', e as unknown as PluginDropdownValue)}
          />
        </FlexColumn>
        {errors?.plugin?.value?.message && (
          <ErrorTextContainer>
            <ErrorText size={3}>{errors?.plugin?.value?.message}</ErrorText>
          </ErrorTextContainer>
        )}
        <StyledInput
          required={true}
          label={<BodyText size={1}>Name</BodyText>}
          placeholder={'Task name here'}
          value={formData.name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            handleInputChange('name', e.target.value);
          }}
        />
        {errors?.name?.message && (
          <ErrorTextContainer justify={'flex-start'}>
            <ErrorText size={3}>{errors?.name?.message}</ErrorText>
          </ErrorTextContainer>
        )}
        <Textarea
          label={<BodyText size={1}>Description</BodyText>}
          placeholder={'Task description here'}
          value={formData.description}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            handleInputChange('description', e.target.value);
          }}
        />
        {errors?.description?.message && (
          <ErrorTextContainer justify={'flex-start'}>
            <ErrorText size={3}>{errors?.description?.message}</ErrorText>
          </ErrorTextContainer>
        )}
        <FlexColumn align={'start'} gap={8}>
          <div>
            <BodyText size={1} scale={'sm'}>
              Task Categories
            </BodyText>
          </div>
          <div style={{ width: '100%' }}>
            <AppCategoriesSelect
              categories={appCategories}
              selectedIds={formData.categories}
              handleChange={(ids) => handleInputChange('categories', ids)}
            />
          </div>
          {errors?.categories?.message && (
            <ErrorTextContainer justify={'flex-start'}>
              <ErrorText size={3}>{errors?.categories?.message}</ErrorText>
            </ErrorTextContainer>
          )}
        </FlexColumn>
        {SelectedFormControls ? (
          <SelectedFormControls.formComponent
            metadata={null}
            errors={errors}
            onChange={(value: any) => {
              handleInputChange('pluginMetadata', value);
            }}
          />
        ) : null}
        <Textarea
          label={<BodyText size={1}>Custom metadata</BodyText>}
          placeholder={'Custom JSON'}
          value={formData.customMetadata?.toString() || undefined}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            handleInputChange('customMetadata', e.target.value);
          }}
        />
        {errors?.customMetadata?.message && (
          <ErrorTextContainer justify={'flex-start'}>
            <ErrorText size={3}>{errors?.customMetadata?.message}</ErrorText>
          </ErrorTextContainer>
        )}
        <div>
          <RewardValueControl
            rewardValues={formData.rewards}
            rewardTypes={rewardsTypes}
            onChange={(updatedRewards: RewardsValue[]) => {
              handleInputChange('rewards', updatedRewards);
            }}
          />
          {errors?.rewards?.message && (
            <ErrorTextContainer justify={'flex-start'} top={'46px'}>
              <ErrorText size={3}>{errors?.rewards?.message}</ErrorText>
            </ErrorTextContainer>
          )}
        </div>
        <FlexRow>
          <AutomationsControlsBlock
            automations={formData.automations}
            onChange={(updatedAutomations: AutomationRequest[]) => {
              handleInputChange('automations', updatedAutomations);
            }}
            errors={errors?.automations}
          />
        </FlexRow>
      </FlexColumn>
      <FlexRow gap={10}>
        <StyledDismissButton color={'secondaryBlue'} onClick={handleOnDismiss}>
          Cancel
        </StyledDismissButton>
        <StyledConfirmButton color="primaryBlue" onClick={sentData}>
          Create
        </StyledConfirmButton>
      </FlexRow>
    </FlexColumn>
  );
};
